import React from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Breadcrumbs,
  Flex,
  H1,
  H4,
  P,
  Tab,
  Tag,
} from '@decisiv/ui-components';
import { t } from '@lingui/macro';

import Helmet from '../../components/SEO/Helmet';
import { encryptStorage } from '../../utils/storage';
import useBetaFeatures from '../../hooks/useBetaFeatures';
import {
  ProfileHeader,
  ProfileHeaderInfo,
  ProfileContent,
  ProfileSection,
  ProfileTabs,
  SectionHeader,
  SectionTitle,
  InfoRow,
} from './style';
import { formatLongDateTime } from '../../utils/dates';
import FeaturesTab from './FeaturesTab';
import AppearanceSection from './AppearanceSection';
import HomeLayoutSection from './HomeLayoutSection';

function InfoField({ label, value }) {
  return (
    <InfoRow>
      <H4 color="alaskanHusky">{label}</H4>
      <P weight="semibold">{value}</P>
    </InfoRow>
  );
}

InfoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

export default function ProfilePage() {
  const userSession = encryptStorage.getItem('userSession');
  const session =
    typeof userSession === 'string' ? JSON.parse(userSession) : userSession;

  const lastLogin = encryptStorage.getItem('lastLogin');

  const fullName = `${session?.first_name || ''} ${
    session?.last_name || ''
  }`.trim();
  const isAdmin = session?.['is_global_admin?'];
  const role = isAdmin ? t`Global Admin` : t`User`;
  const email = session?.email || t`N/A`;
  const createdAt = session?.created_at
    ? formatLongDateTime(session.created_at)
    : t`N/A`;
  const lastLoginFormatted = lastLogin ? formatLongDateTime(lastLogin) : t`N/A`;
  const { isEnabled } = useBetaFeatures();

  return (
    <>
      <Helmet title={t`Profile`} description={t`User Profile`} />
      <Flex padding={1}>
        <Breadcrumbs
          path={[{ to: '/integrations', text: 'Integrations' }]}
          current="Profile"
        />
      </Flex>

      <ProfileHeader>
        <Avatar name={fullName || 'User'} size="large" />
        <ProfileHeaderInfo>
          <Flex alignItems="center" style={{ gap: '0.75rem' }}>
            <H1>{fullName || t`N/A`}</H1>
            <Tag
              text={role}
              color={isAdmin ? 'success' : 'information'}
              variant="outline"
            />
          </Flex>
          <P color="alaskanHusky">{email}</P>
        </ProfileHeaderInfo>
      </ProfileHeader>

      <ProfileTabs
        renderHiddenTabsContent={false}
        size="large"
        defaultSelectedId="profile-tab"
      >
        <Tab title={t`Profile`} id="profile-tab">
          <ProfileContent>
            <ProfileSection>
              <SectionHeader>
                <SectionTitle>{t`Account Information`}</SectionTitle>
              </SectionHeader>
              <InfoField
                label={t`Username`}
                value={session?.username || t`N/A`}
              />
              <InfoField label={t`Email`} value={email} />
              <InfoField label={t`Role`} value={role} />
            </ProfileSection>

            <ProfileSection>
              <SectionHeader>
                <SectionTitle>{t`Activity`}</SectionTitle>
              </SectionHeader>
              <InfoField label={t`Account Created`} value={createdAt} />
              <InfoField label={t`Last Login`} value={lastLoginFormatted} />
            </ProfileSection>

            {isEnabled('themesManagement') && <AppearanceSection />}
            {isEnabled('homeLayouts') && <HomeLayoutSection />}
          </ProfileContent>
        </Tab>
        {isAdmin && (
          <Tab title={t`Features`} id="features-tab">
            <ProfileContent>
              <FeaturesTab />
            </ProfileContent>
          </Tab>
        )}
      </ProfileTabs>
    </>
  );
}
